<template>
  <div :class="`min-h-screen ${applyThemeClasses('layout.background')}`">
    <!-- Header -->
    <div class="bg-gradient-to-r from-yellow-400 to-orange-500 p-6 text-white">
      <h1 class="text-3xl font-bold mb-2">🏆 Top Performers</h1>
      <p class="text-yellow-100">Best race times across all circuits and distances</p>
    </div>

    <!-- Time Period Tabs -->
    <div :class="`border-b ${applyThemeClasses('layout.secondaryBackground')}`">
      <div class="max-w-6xl mx-auto px-6">
        <div class="flex space-x-1">
          <button
            @click="selectedTimePeriod = 'monthly'"
            :class="[
              'px-6 py-3 font-medium transition-colors',
              selectedTimePeriod === 'monthly'
                ? `${applyThemeClasses('layout.card')} text-blue-600 border-t-2 border-blue-600`
                : `${applyThemeClasses('text.secondary')} ${darkModeStore.isDark ? 'hover:text-gray-300' : 'hover:text-gray-800'}`
            ]"
          >
            📅 Monthly Records
          </button>
          <button
            @click="selectedTimePeriod = 'allTime'"
            :class="[
              'px-6 py-3 font-medium transition-colors',
              selectedTimePeriod === 'allTime'
                ? `${applyThemeClasses('layout.card')} text-blue-600 border-t-2 border-blue-600`
                : `${applyThemeClasses('text.secondary')} ${darkModeStore.isDark ? 'hover:text-gray-300' : 'hover:text-gray-800'}`
            ]"
          >
            🌟 All-Time Records
          </button>
        </div>
      </div>
    </div>

    <!-- Race Distance Tabs -->
    <div :class="`border-b ${applyThemeClasses('layout.card')}`">
      <div class="max-w-6xl mx-auto px-6">
        <div class="flex space-x-1">
          <button
            @click="selectedRaceType = 'short'"
            :class="[
              'px-4 py-2 text-sm font-medium transition-colors',
              selectedRaceType === 'short'
                ? `${darkModeStore.isDark ? 'bg-blue-900/50' : 'bg-blue-100'} text-blue-${darkModeStore.isDark ? '300' : '700'} border-b-2 border-blue-600`
                : `${applyThemeClasses('text.secondary')} ${darkModeStore.isDark ? 'hover:text-gray-300' : 'hover:text-gray-800'}`
            ]"
          >
            ⚡ Sprint (800m)
          </button>
          <button
            @click="selectedRaceType = 'medium'"
            :class="[
              'px-4 py-2 text-sm font-medium transition-colors',
              selectedRaceType === 'medium'
                ? `${darkModeStore.isDark ? 'bg-blue-900/50' : 'bg-blue-100'} text-blue-${darkModeStore.isDark ? '300' : '700'} border-b-2 border-blue-600`
                : `${applyThemeClasses('text.secondary')} ${darkModeStore.isDark ? 'hover:text-gray-300' : 'hover:text-gray-800'}`
            ]"
          >
            🏃 Classic (2000m)
          </button>
          <button
            @click="selectedRaceType = 'long'"
            :class="[
              'px-4 py-2 text-sm font-medium transition-colors',
              selectedRaceType === 'long'
                ? `${darkModeStore.isDark ? 'bg-blue-900/50' : 'bg-blue-100'} text-blue-${darkModeStore.isDark ? '300' : '700'} border-b-2 border-blue-600`
                : `${applyThemeClasses('text.secondary')} ${darkModeStore.isDark ? 'hover:text-gray-300' : 'hover:text-gray-800'}`
            ]"
          >
            🏃‍♂️ Endurance (5000m)
          </button>
          <button
            @click="selectedRaceType = 'earnings'"
            :class="[
              'px-4 py-2 text-sm font-medium transition-colors',
              selectedRaceType === 'earnings'
                ? `${darkModeStore.isDark ? 'bg-blue-900/50' : 'bg-blue-100'} text-blue-${darkModeStore.isDark ? '300' : '700'} border-b-2 border-blue-600`
                : `${applyThemeClasses('text.secondary')} ${darkModeStore.isDark ? 'hover:text-gray-300' : 'hover:text-gray-800'}`
            ]"
          >
            💰 Top Earners
          </button>
        </div>
      </div>
    </div>

    <!-- Records Table -->
    <div class="max-w-6xl mx-auto px-6 py-8">
      <div :class="`rounded-lg shadow-lg overflow-hidden ${applyThemeClasses('layout.card')}`">
        <!-- Table Header -->
        <div :class="`px-6 py-4 border-b ${applyThemeClasses('layout.secondaryBackground')}`">
          <h2 :class="`text-xl font-semibold ${applyThemeClasses('text.primary')}`">
            {{ getTableTitle() }}
            <span class="text-sm text-gray-500 ml-2">({{ currentRecords.length }}/10 records)</span>
          </h2>
          <div v-if="selectedTimePeriod === 'monthly' && getCurrentMonth() === 1" class="text-sm text-orange-600 mt-1">
            ⚠️ First month - records will differentiate after day 32
          </div>
          <div v-else-if="selectedTimePeriod === 'monthly'" class="text-sm text-blue-600 mt-1">
            📅 Records from Month {{ getCurrentMonth() }} (Days {{ (getCurrentMonth() - 1) * 32 + 1 }}-{{ getCurrentMonth() * 32 }})
          </div>
          <div v-if="hasPlayerRecords" class="text-sm text-yellow-600 mt-2 flex items-center">
            <span class="inline-block w-4 h-4 bg-yellow-400/30 border border-yellow-400 rounded mr-2"></span>
            Your chocobos are highlighted in gold - Monthly winners receive prizes!
          </div>
        </div>

        <!-- Records Display -->
        <div v-if="currentRecords.length === 0" class="p-8 text-center text-gray-500">
          <div class="text-4xl mb-4">🏁</div>
          <p class="text-lg">No records yet for this category</p>
          <p class="text-sm">Complete some races to see top performers!</p>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full">
            <thead :class="applyThemeClasses('layout.secondaryBackground')">
              <tr>
                <th :class="`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${applyThemeClasses('text.secondary')}`">Rank</th>
                <th v-if="selectedRaceType === 'earnings'" :class="`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${applyThemeClasses('text.secondary')}`">Total Earnings</th>
                <th v-else :class="`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${applyThemeClasses('text.secondary')}`">Time</th>
                <th :class="`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${applyThemeClasses('text.secondary')}`">Chocobo</th>
                <th v-if="selectedRaceType === 'earnings'" :class="`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${applyThemeClasses('text.secondary')}`">Races</th>
                <th v-else :class="`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${applyThemeClasses('text.secondary')}`">Jockey</th>
                <th :class="`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${applyThemeClasses('text.secondary')}`">Owner</th>
                <th v-if="selectedRaceType === 'earnings'" :class="`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${applyThemeClasses('text.secondary')}`">Avg Earnings</th>
                <th v-else :class="`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${applyThemeClasses('text.secondary')}`">Circuit</th>
                <th v-if="selectedRaceType !== 'earnings'" :class="`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${applyThemeClasses('text.secondary')}`">Date</th>
              </tr>
            </thead>
            <tbody :class="`divide-y ${applyThemeClasses('layout.border')}`">
              <tr 
                v-for="(record, index) in currentRecords" 
                :key="`${selectedTimePeriod}-${selectedRaceType}-${index}-${record?.chocoboId || 'unknown'}-${record?.date || 0}`"
                :class="[
                  `transition-colors ${darkModeStore.isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`,
                  isPlayerChocobo(record) ? (darkModeStore.isDark ? 'bg-yellow-600/20 border-2 border-yellow-400/50' : 'bg-yellow-100/50 border-2 border-yellow-400') : '',
                  !isPlayerChocobo(record) && index === 0 ? (darkModeStore.isDark ? 'bg-yellow-900/30 border-yellow-500/50' : 'bg-yellow-50') : '',
                  !isPlayerChocobo(record) && index === 1 ? (darkModeStore.isDark ? 'bg-gray-700/50 border-gray-400/50' : 'bg-gray-50') : '',
                  !isPlayerChocobo(record) && index === 2 ? (darkModeStore.isDark ? 'bg-orange-900/30 border-orange-500/50' : 'bg-orange-50') : ''
                ]"
              >
                <!-- Rank -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <span v-if="index === 0" class="text-2xl">🥇</span>
                    <span v-else-if="index === 1" class="text-2xl">🥈</span>
                    <span v-else-if="index === 2" class="text-2xl">🥉</span>
                    <span v-else :class="`text-lg font-semibold ${applyThemeClasses('text.secondary')}`">{{ index + 1 }}</span>
                    <span :class="`ml-2 text-sm font-medium ${applyThemeClasses('text.primary')}`">{{ getRankSuffix(index + 1) }}</span>
                  </div>
                </td>

                <!-- Time or Earnings -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <div v-if="selectedRaceType === 'earnings'" class="text-lg font-bold text-green-600">{{ record?.earnings?.toLocaleString() || '0' }}G</div>
                  <div v-else class="text-lg font-bold text-blue-600">{{ record?.formattedTime || 'N/A' }}</div>
                  <div v-if="selectedRaceType !== 'earnings'" :class="`text-xs ${applyThemeClasses('text.secondary')}`">{{ record?.speed || 'N/A' }}</div>
                </td>

                <!-- Chocobo -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="flex-shrink-0 h-8 w-8">
                      <div :class="getChocoboColorClass(record?.chocoboColor)" class="h-8 w-8 rounded-full flex items-center justify-center">
                        <span class="text-white text-sm font-bold">🐦</span>
                      </div>
                    </div>
                    <div class="ml-3">
                      <div :class="`text-sm font-medium ${applyThemeClasses('text.primary')}`">{{ record?.chocoboName || 'Unknown' }}</div>
                      <div :class="`text-xs ${applyThemeClasses('text.secondary')}`">Level {{ record?.chocoboLevel || 'N/A' }}</div>
                    </div>
                  </div>
                </td>

                <!-- Jockey or Race Count -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <div v-if="selectedRaceType === 'earnings'" :class="`text-sm font-medium ${applyThemeClasses('text.primary')}`">{{ record?.raceCount || 0 }} races</div>
                  <div v-else :class="`text-sm font-medium ${applyThemeClasses('text.primary')}`">{{ record?.jockeyName || 'Unknown' }}</div>
                </td>

                <!-- Owner -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div>
                      <div :class="`text-sm font-medium ${applyThemeClasses('text.primary')}`">{{ record?.ownerName || 'Unknown' }}</div>
                      <div :class="`text-xs ${applyThemeClasses('text.secondary')}`">{{ record?.ranchName || 'Unknown Ranch' }}</div>
                    </div>
                    <div v-if="isPlayerChocobo(record)" class="ml-2">
                      <span class="text-xs bg-yellow-500 text-white px-2 py-1 rounded-full font-medium">YOU</span>
                    </div>
                  </div>
                </td>

                <!-- Circuit or Avg Earnings -->
                <td v-if="selectedRaceType === 'earnings'" class="px-6 py-4 whitespace-nowrap">
                  <div :class="`text-sm font-medium ${applyThemeClasses('text.primary')}`">{{ record?.avgEarnings?.toLocaleString() || '0' }}G</div>
                  <div :class="`text-xs ${applyThemeClasses('text.secondary')}`">per race</div>
                </td>
                <td v-else class="px-6 py-4 whitespace-nowrap">
                  <span :class="getCircuitBadgeClass(record?.circuit)" class="px-2 py-1 text-xs font-semibold rounded-full">
                    {{ getCircuitDisplayName(record?.circuit) }}
                  </span>
                </td>

                <!-- Date (only for non-earnings) -->
                <td v-if="selectedRaceType !== 'earnings'" :class="`px-6 py-4 whitespace-nowrap text-sm ${applyThemeClasses('text.secondary')}`">
                  <div v-if="formatRecordDate(record?.date).date !== 'N/A'">
                    <div>{{ formatRecordDate(record?.date).date }}</div>
                    <div class="text-xs text-gray-400">({{ formatRecordDate(record?.date).daysAgo }} days ago)</div>
                  </div>
                  <div v-else>N/A</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Statistics Panel -->
      <div class="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        

        <div class="bg-white p-6 rounded-lg shadow">
          <h3 class="text-lg font-semibold text-gray-800 mb-2">🏆 This Period</h3>
          <div class="text-2xl font-bold text-green-600">{{ selectedTimePeriod === 'monthly' ? 'This Month' : 'All Time' }}</div>
          <div class="text-sm text-gray-500 mt-1">{{ getTotalRecords() }} total records</div>
          <div class="text-xs text-gray-400 mt-2">
            Day {{ gameStore.currentDay }} (Month {{ getCurrentMonth() }})
            <br v-if="selectedTimePeriod === 'monthly' && getCurrentMonth() === 1">
            <span v-if="selectedTimePeriod === 'monthly' && getCurrentMonth() === 1" class="text-orange-500">
              First month - monthly records will reset after day 32
            </span>
          </div>
        </div>

        <!-- Player Monthly Wins Alert -->
        <div v-if="selectedTimePeriod === 'monthly' && playerMonthlyWins.length > 0" class="bg-yellow-50 border-2 border-yellow-400 p-6 rounded-lg shadow">
          <h3 class="text-lg font-semibold text-yellow-800 mb-2">🎉 Monthly Leaders!</h3>
          <div class="text-sm text-yellow-700">
            Your chocobos are leading in:
            <ul class="mt-1">
              <li v-for="win in playerMonthlyWins" :key="win" class="flex items-center">
                <span class="text-green-600 mr-1">✓</span>
                {{ getRaceTypeDisplayName(win) }}
              </li>
            </ul>
            <div class="mt-2 text-xs">Prizes awarded at month end!</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { PerformanceRecordsService } from '@/services/performanceRecordsService'
import { useGameStore } from '@/store/game'
import { GaiaCalendar } from '@/utils/gaiaCalendar'
import { useDarkModeStore } from '@/store/darkMode'
import { applyThemeClasses } from '@/utils/themeColors'

export default {
  name: 'TopPerformers',
  
  setup() {
    const gameStore = useGameStore()
    const darkModeStore = useDarkModeStore()
    return { gameStore, darkModeStore, applyThemeClasses }
  },
  
  data() {
    return {
      selectedTimePeriod: 'monthly', // 'monthly' or 'allTime'
      selectedRaceType: 'short'       // 'short', 'medium', 'long', 'earnings'
    }
  },

  computed: {

    performanceRecords() {
      return this.gameStore.performanceRecords || PerformanceRecordsService.initializeRecords()
    },

    currentRecords() {
      try {
        const records = PerformanceRecordsService.getTopPerformers(
          this.performanceRecords, 
          this.selectedTimePeriod, 
          this.selectedRaceType
        )
        
        return Array.isArray(records) ? records.filter(record => record != null) : []
      } catch (error) {
        console.warn('Error getting current records:', error)
        return []
      }
    },

    fastestRecord() {
      try {
        const stats = PerformanceRecordsService.getRecordStats(this.performanceRecords)
        return stats?.fastestOverall || null
      } catch (error) {
        console.warn('Error getting fastest record:', error)
        return null
      }
    },

    hasPlayerRecords() {
      return this.currentRecords.some(record => this.isPlayerChocobo(record))
    },

    playerMonthlyWins() {
      if (this.selectedTimePeriod !== 'monthly') return []
      
      const wins = []
      const raceTypes = ['short', 'medium', 'long']
      
      for (const raceType of raceTypes) {
        const records = PerformanceRecordsService.getTopPerformers(
          this.performanceRecords,
          'monthly',
          raceType
        )
        
        if (records && records.length > 0 && this.isPlayerChocobo(records[0])) {
          wins.push(raceType)
        }
      }
      
      return wins
    }
  },

  methods: {
    formatRecordDate(day) {
      if (!day || day === 'N/A') return 'N/A'
      
      // Format the date using GaiaCalendar
      const formattedDate = GaiaCalendar.formatGameDate(day)
      
      // Calculate days ago
      const daysAgo = this.gameStore.currentDay - day
      
      return {
        date: formattedDate,
        daysAgo: daysAgo
      }
    },
    
    getTableTitle() {
      const timePeriod = this.selectedTimePeriod === 'monthly' ? 'Monthly' : 'All-Time'
      const raceType = this.getRaceTypeDisplayName(this.selectedRaceType)
      return `${timePeriod} ${raceType} Records`
    },

    getRaceTypeDisplayName(raceType) {
      const names = {
        short: 'Sprint (800m)',
        medium: 'Classic (2000m)', 
        long: 'Endurance (5000m)',
        earnings: 'Top Earners'
      }
      return names[raceType] || raceType
    },

    getCircuitDisplayName(circuitId) {
      if (!circuitId) return 'Unknown'
      const names = {
        regional: 'Regional',
        continental: 'Continental', 
        transcontinental: 'Trans-Continental',
        global: 'Global',
        saucer: 'Gold Saucer'
      }
      return names[circuitId] || circuitId
    },

    getCircuitBadgeClass(circuitId) {
      if (!circuitId) return 'bg-gray-100 text-gray-800'
      const classes = {
        regional: 'bg-green-100 text-green-800',
        continental: 'bg-blue-100 text-blue-800',
        transcontinental: 'bg-purple-100 text-purple-800', 
        global: 'bg-red-100 text-red-800',
        saucer: 'bg-yellow-100 text-yellow-800'
      }
      return classes[circuitId] || 'bg-gray-100 text-gray-800'
    },

    getChocoboColorClass(color) {
      if (!color) return 'bg-gray-400'
      const classes = {
        yellow: 'bg-yellow-400',
        blue: 'bg-blue-500',
        green: 'bg-green-500',
        black: 'bg-gray-800',
        pink: 'bg-pink-400',
        gold: 'bg-yellow-500'
      }
      return classes[color] || 'bg-gray-400'
    },

    getRankSuffix(rank) {
      if (rank === 1) return ''
      if (rank === 2) return ''
      if (rank === 3) return ''
      
      const lastDigit = rank % 10
      const lastTwoDigits = rank % 100
      
      if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
        return 'th'
      }
      
      switch (lastDigit) {
        case 1: return 'st'
        case 2: return 'nd' 
        case 3: return 'rd'
        default: return 'th'
      }
    },

    getRecordCount(raceType) {
      try {
        const records = this.selectedTimePeriod === 'monthly' 
          ? this.performanceRecords.monthlyRecords 
          : this.performanceRecords.allTimeRecords
        return records?.[raceType]?.length || 0
      } catch (error) {
        console.warn('Error getting record count:', error)
        return 0
      }
    },

    getTotalRecords() {
      try {
        const records = this.selectedTimePeriod === 'monthly' 
          ? this.performanceRecords.monthlyRecords 
          : this.performanceRecords.allTimeRecords
        
        if (!records) return 0
        
        return (records.short?.length || 0) + 
               (records.medium?.length || 0) + 
               (records.long?.length || 0)
      } catch (error) {
        console.warn('Error getting total records:', error)
        return 0
      }
    },

    getCurrentMonth() {
      // Same formula as MonthlyRaceManager.getMonthFromDay
      return Math.floor((this.gameStore.currentDay - 1) / 32) + 1
    },

    isPlayerChocobo(record) {
      if (!record || !record.chocoboId || !this.gameStore.playerOwner) return false
      return this.gameStore.playerOwner.chocoboIds.includes(record.chocoboId)
    }
  }
}
</script>